import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Shell from './tuteur/Shell';
import Lecon, { Regle } from './Lecon';

// ── Atelier d'écriture (page PERSONNELLE, propriétaire uniquement) ──────────
// Accès via ?ecrire. Données : /api/ecrire/* (401/403 → redirection silencieuse).
//
// Vise le subtest Письмо du ТРКИ-2. Tout le reste du site entraîne la
// reconnaissance ; ici on PRODUIT, parce que deux subtests sur cinq sont de la
// production, qu'il faut 66 % à chacun et qu'un seul subtest raté fait échouer
// l'ensemble.
//
// Conditions d'examen assumées :
//   - la consigne est EN RUSSE, comme le jour J ;
//   - un chronomètre tourne (il n'interrompt pas : il informe, comme la barre du
//     quiz — juger le temps ne ferait pas mieux écrire) ;
//   - le compteur de mots signale la fourchette attendue, hors de laquelle des
//     points se perdent quel que soit le contenu.
//
// La correction n'est PAS automatique : elle est écrite hors ligne puis rapatriée
// en base. Pas de clé d'API en production, et une correction relue vaut mieux
// qu'une correction générée.

const TOKEN_KEY = 'motrusse_token';
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";
const DRAFT_KEY = 'motrusse_ecrire_brouillon';

const TACHE_FR = {
  1: 'Tâche 1 : lettre non officielle, appuyée sur des documents',
  2: 'Tâche 2 : lettre officielle',
  3: 'Tâche 3 : recommandation d\'une personne',
};

// Trois marches avant l'épreuve. La page ne proposait que la dernière, ce qui
// revenait à faire passer l'examen à quelqu'un qui n'a jamais écrit une phrase.
const NIVEAUX = {
  1: { titre: 'Une phrase', sous: "Une seule phrase, avec la structure à employer. C'est là qu'on commence." },
  2: { titre: 'Un paragraphe', sous: 'Quarante à soixante-dix mots, une seule intention, sans le cadre de la lettre.' },
  3: { titre: "La lettre d'examen", sous: 'Format ТРКИ-2 : consigne en russe, chronomètre, 150 à 200 mots.' },
};

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const countWords = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const Ru = ({ children, className = '' }) => (
  <span lang="ru" style={{ fontFamily: SERIF }} className={className}>{children}</span>
);

// Cours d'entrée de l'atelier. C'est ici que le besoin était le plus criant : on
// ne devine pas les formules d'une lettre officielle russe ni ce que le barème
// récompense. Sans ce cours, la page demandait de produire un genre jamais vu.
const CoursEcrire = () => (
  <Lecon id="ecrire" titre="Avant de commencer : ce qu'attend le correcteur">
    <p>
      Le subtest Письмо dure 55 minutes pour trois tâches, et il faut 66 % comme
      dans chacun des quatre autres. On y perd rarement des points sur le
      vocabulaire : on en perd sur la <strong className="text-slate-200">structure</strong>, sur le
      <strong className="text-slate-200"> registre</strong> et sur les points de la consigne qu'on a oubliés.
    </p>

    <div className="flex flex-col gap-1.5 border-t border-slate-700/60 pt-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">Les quatre parties, toujours dans cet ordre</p>
      <Regle terme="Обращение" quand="l'appel, suivi d'une virgule ou d'un point d'exclamation" exemple="Уважаемый Иван Петрович! / Дорогая Аня," />
      <Regle terme="Зачин" quand="pourquoi tu écris, en une phrase" exemple="Пишу тебе, потому что… / Обращаюсь к Вам в связи с…" />
      <Regle terme="Основная часть" quand="le contenu, un paragraphe par point de la consigne" exemple="во-первых… во-вторых… кроме того…" />
      <Regle terme="Концовка" quand="la formule de clôture, puis ton prénom" exemple="С уважением, / Обнимаю," />
    </div>

    <div className="flex flex-col gap-1.5 border-t border-slate-700/60 pt-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">Officiel ou familier : ne jamais mélanger</p>
      <Regle terme="Officiel" quand="vouvoiement, Вы avec majuscule, pas de contractions ni d'exclamations" exemple="Уважаемый… / Прошу Вас… / Заранее благодарю. / С уважением," />
      <Regle terme="Familier" quand="tutoiement, questions, marques d'affect" exemple="Привет! / Как ты? / Слушай… / Целую, Обнимаю," />
    </div>

    <p className="text-slate-400 border-t border-slate-700/60 pt-3">
      <strong className="text-slate-200">La méthode qui rapporte le plus :</strong> avant d'écrire, relis la
      consigne et compte les demandes. Elles sont presque toujours au nombre de
      deux ou trois : recommander, justifier, comparer. Un paragraphe pour chacune.
      Une lettre magnifique qui oublie une demande passe sous la barre ; une lettre
      simple qui les traite toutes passe.
    </p>

    <p className="text-slate-400">
      <strong className="text-slate-200">Cinq connecteurs</strong> suffisent à structurer et se voient tout de suite
      au barème : <span lang="ru" className="text-blue-300">во-первых</span> (d'abord),
      <span lang="ru" className="text-blue-300"> кроме того</span> (en outre),
      <span lang="ru" className="text-blue-300"> поэтому</span> (donc),
      <span lang="ru" className="text-blue-300"> однако</span> (cependant),
      <span lang="ru" className="text-blue-300"> в отличие от</span> (à la différence de).
    </p>

    <p className="text-slate-500 text-[13px]">
      Compte les mots : trop court est sanctionné, trop long fait perdre du temps sur
      les autres tâches. Le compteur en bas de page t'indique la fourchette. Et à
      l'examen, le dictionnaire russe-russe papier est autorisé, pas le bilingue.
    </p>
  </Lecon>
);

export default function Writing({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading'); // loading | error | liste | ecriture
  const [sujets, setSujets] = useState([]);
  const [copies, setCopies] = useState([]);
  const [sujet, setSujet] = useState(null);
  const [texte, setTexte] = useState('');
  const [secondes, setSecondes] = useState(0);
  const [sending, setSending] = useState(false);
  const [openCopie, setOpenCopie] = useState(null);
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  const areaRef = useRef(null);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const charger = useCallback(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    Promise.all([
      fetch('/api/ecrire/sujets', { headers: auth }),
      fetch('/api/ecrire/copies', { headers: auth }),
    ]).then(async ([a, b]) => {
      if (a.status === 401 || a.status === 403) { leave(); return; }
      if (!a.ok || !b.ok) throw new Error('http');
      setSujets(await a.json());
      const cps = await b.json();
      setCopies(cps);
      // La correction la plus récente s'ouvre d'elle-même : sans ça il fallait
      // savoir que la ligne était cliquable, et les corrections passaient
      // inaperçues (« je ne vois pas les corrections »).
      const derniere = cps.find((c) => c.corrige || c.commentaire);
      if (derniere) setOpenCopie(derniere.id);
      setPhase('liste');
    }).catch(() => setPhase('error'));
  }, [token, auth]);

  useEffect(charger, [charger]);

  // Chronomètre : il ne tourne QUE pendant l'écriture. Pas de compte à rebours
  // qui coupe la copie — l'examen laisse finir la phrase commencée, et couper
  // ici punirait sans rien apprendre.
  useEffect(() => {
    if (phase !== 'ecriture') return;
    const id = setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Brouillon local : une copie de 200 mots perdue par un rechargement, et on
  // n'écrit plus jamais dans cette page.
  useEffect(() => {
    if (phase !== 'ecriture' || !sujet) return;
    // Rien d'écrit = pas de brouillon. Auparavant, OUVRIR un sujet puis revenir
    // en arrière fabriquait un brouillon vide impossible à effacer.
    if (!texte.trim()) {
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      return;
    }
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ slug: sujet.slug, texte, secondes })); } catch {}
  }, [texte, secondes, phase, sujet]);

  const commencer = (s, reprise) => {
    setSujet(s);
    setTexte(reprise?.texte ?? '');
    setSecondes(reprise?.secondes ?? 0);
    setPhase('ecriture');
    setTimeout(() => areaRef.current?.focus(), 50);
  };

  // Brouillon : un ÉTAT, pas une lecture figée au montage. Il était mémoïsé une
  // fois pour toutes, donc la bannière survivait à sa propre suppression.
  const relireBrouillon = () => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch { return null; }
  };
  const [brouillon, setBrouillon] = useState(relireBrouillon);
  const supprimerBrouillon = useCallback(() => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setBrouillon(null);
  }, []);

  const rendre = useCallback(() => {
    if (!sujet || sending || !texte.trim()) return;
    setSending(true);
    fetch('/api/ecrire/copie', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ slug: sujet.slug, texte, secondes }),
    })
      .then((r) => r.json())
      .then(() => {
        supprimerBrouillon();
        setTexte(''); setSecondes(0); setSujet(null);
        charger();
      })
      .catch(() => {})
      .finally(() => setSending(false));
  }, [sujet, texte, secondes, sending, auth, charger, supprimerBrouillon]);

  if (phase === 'loading') {
    return (
      <Shell actif="/?ecrire" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?ecrire" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">Impossible de charger l'atelier.</p>
          <button onClick={charger} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            Réessayer
          </button>
        </div>
      </Shell>
    );
  }

  // ── Écriture ─────────────────────────────────────────────────────────────
  if (phase === 'ecriture' && sujet) {
    const n = countWords(texte);
    const court = n < sujet.mots_min;
    const long = n > sujet.mots_max;
    const depasse = secondes > sujet.minutes * 60;
    return (
      <Shell actif="/?ecrire" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-lg md:text-xl font-bold">
            {NIVEAUX[sujet.niveau]?.titre}
            {sujet.tache > 0 && <span className="text-slate-500 font-normal text-sm"> · {TACHE_FR[sujet.tache]}</span>}
          </h1>
          <span className={`text-sm tabular-nums shrink-0 ${depasse ? 'text-amber-400' : 'text-slate-500'}`}>
            {mmss(secondes)} <span className="text-slate-600">/ {sujet.minutes}:00</span>
          </span>
        </div>

        {sujet.niveau === 3 && <CoursEcrire />}

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col gap-3">
          <Ru className="text-base leading-relaxed text-white">{sujet.consigne}</Ru>
          {sujet.appui && (
            <pre className="text-sm text-slate-300 whitespace-pre-wrap border-l-2 border-slate-600 pl-3" lang="ru" style={{ fontFamily: SERIF }}>
              {sujet.appui}
            </pre>
          )}
          <p className="text-xs text-slate-500">
            {sujet.genre} · {sujet.mots_min} à {sujet.mots_max} mots
          </p>
        </div>

        <textarea
          ref={areaRef}
          lang="ru"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          spellCheck="false"
          placeholder="Пишите здесь…"
          style={{ fontFamily: SERIF }}
          className="w-full min-h-[19rem] bg-slate-800 border border-slate-700 rounded-xl p-4 text-base leading-relaxed text-white placeholder-slate-600 focus:border-blue-500 outline-none resize-y"
        />

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={rendre}
            disabled={sending || !texte.trim()}
            className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium transition-colors"
          >
            {sending ? 'Envoi…' : 'Rendre la copie'}
          </button>
          <span className={`text-sm tabular-nums ${court || long ? 'text-amber-400' : 'text-slate-400'}`}>
            {n} mots
            {court && ` · il en manque ${sujet.mots_min - n}`}
            {long && ` · ${n - sujet.mots_max} de trop`}
          </span>
          <button
            onClick={() => {
              // Sans texte, on ne laisse RIEN derrière soi.
              if (!texte.trim()) supprimerBrouillon();
              else setBrouillon({ slug: sujet.slug, texte, secondes });
              setPhase('liste'); setSujet(null);
            }}
            className="ml-auto text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            {texte.trim() ? 'Laisser en brouillon' : 'Revenir à la liste'}
          </button>
        </div>
        <p className="text-xs text-slate-600">
          Le brouillon est gardé dans ce navigateur. La copie rendue part en base et
          n'écrase jamais une copie précédente : refaire le même sujet dans trois mois,
          c'est la seule façon de mesurer une progression en production.
        </p>
      </Shell>
    );
  }

  // ── Liste des sujets et des copies ───────────────────────────────────────
  const parNiveau = [1, 2, 3].map((n) => ({ n, items: sujets.filter((s) => s.niveau === n) }));
  const corrigees = copies.filter((c) => c.corrige || c.commentaire).length;
  const aLire = corrigees;
  // Les copies CORRIGÉES d'abord : c'est ce qu'on vient lire.
  const triees = [...copies].sort((a, b) => {
    const ca = (a.corrige || a.commentaire) ? 1 : 0;
    const cb = (b.corrige || b.commentaire) ? 1 : 0;
    return cb - ca;
  });

  return (
    <Shell actif="/?ecrire" cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex flex-col gap-1">
        <h1 className="text-xl md:text-2xl font-bold">Écrire</h1>
        <p className="text-sm text-slate-400">
          On monte par marches : d'abord une phrase juste, puis un paragraphe, et
          seulement ensuite la lettre d'examen. Inutile d'attaquer par l'épreuve
          finale, c'est le meilleur moyen de ne rien écrire du tout.
        </p>
      </div>

      <CoursEcrire />

      {brouillon && (
        <div className="bg-slate-800 border border-blue-500/40 rounded-xl p-4 flex items-center gap-3 flex-wrap">
          <p className="text-sm text-slate-300">
            Brouillon en cours sur « {brouillon.slug} », {countWords(brouillon.texte || '')} mots.
          </p>
          <button
            onClick={() => {
              const s = sujets.find((x) => x.slug === brouillon.slug);
              if (s) commencer(s, brouillon);
            }}
            className="ml-auto px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-medium transition-colors"
          >
            Reprendre
          </button>
          <button
            onClick={supprimerBrouillon}
            className="text-xs text-slate-500 hover:text-red-400 transition-colors"
          >
            Supprimer
          </button>
        </div>
      )}

      {copies.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Tes copies</h2>
            {aLire > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
                {aLire} correction{aLire > 1 ? 's' : ''} à lire
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {copies.length} rendue{copies.length > 1 ? 's' : ''}, {corrigees} corrigée{corrigees > 1 ? 's' : ''}.
            Les corrections arrivent par lot, elles ne sont pas automatiques.
          </p>
          {triees.map((c) => {
            const ouvert = openCopie === c.id;
            const aCorrection = !!(c.corrige || c.commentaire);
            return (
              <div key={c.id} className={`bg-slate-800 border rounded-xl overflow-hidden ${aCorrection ? 'border-blue-500/50' : 'border-slate-700'}`}>
                <button
                  onClick={() => setOpenCopie(ouvert ? null : c.id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-700/40 transition-colors flex items-baseline gap-2 flex-wrap"
                >
                  <span className="text-sm text-slate-200">{c.slug}</span>
                  <span className="text-[11px] text-slate-500">
                    {c.created_at?.slice(0, 10)} · {c.mots} mots · {mmss(c.secondes || 0)}
                  </span>
                  <span className={`ml-auto text-[11px] ${aCorrection ? 'text-blue-300' : 'text-slate-600'}`}>
                    {aCorrection ? (ouvert ? 'corrigée' : 'corrigée · voir') : 'en attente de correction'}
                  </span>
                </button>
                {ouvert && (
                  <div className="px-4 pb-4 flex flex-col gap-3 border-t border-slate-700/60 pt-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Ce que tu as écrit</p>
                      <Ru className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{c.texte}</Ru>
                    </div>
                    {c.corrige && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Version corrigée</p>
                        <Ru className="text-sm text-blue-200 whitespace-pre-wrap leading-relaxed">{c.corrige}</Ru>
                      </div>
                    )}
                    {c.commentaire && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Ce qui coince</p>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{c.commentaire}</p>
                      </div>
                    )}
                    {c.note != null && (
                      <p className="text-xs text-slate-400">
                        Estimation examen : <span className="text-white font-semibold">{c.note} / 100</span>
                        <span className="text-slate-600"> · il faut 66 pour passer</span>
                      </p>
                    )}
                    {!aCorrection && (
                      <p className="text-xs text-slate-500">
                        Pas encore corrigée. Elle le sera au prochain passage.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {parNiveau.map(({ n, items }) => (
        <section key={n} className="flex flex-col gap-2">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">
              {n}. {NIVEAUX[n].titre}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{NIVEAUX[n].sous}</p>
          </div>
          {items.map((s) => (
            <button
              key={s.slug}
              onClick={() => commencer(s)}
              className="text-left bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-blue-500/60 transition-colors"
            >
              <Ru className="text-sm text-slate-200 leading-relaxed line-clamp-2">{s.consigne}</Ru>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {s.genre} · {s.minutes} min · {s.mots_min}-{s.mots_max} mots
                {s.rendus > 0 && ` · ${s.rendus} copie${s.rendus > 1 ? 's' : ''} rendue${s.rendus > 1 ? 's' : ''}`}
              </p>
            </button>
          ))}
        </section>
      ))}

      <a href="/?tuteur" className="self-start text-sm text-blue-300 hover:text-blue-200 transition-colors">
        Retour au tuteur
      </a>
    </Shell>
  );
}
